import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  login as loginService,
  register as registerService,
  registerPhoneUser as registerPhoneUserService,
  logout as logoutService,
  onIdTokenChange,
  refreshToken,
  resendVerificationEmail,
  getUserCPF,
} from '../services/authService';
import { auth } from '../services/firebase';
import { saveItem, removeItem } from '../utils/storage';
import { STORAGE_KEYS } from '../utils/constants';
import { verifyAdmin } from '../services/adminService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [cpfVerified, setCpfVerified] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  // Incremented on logout so that any in-flight listener callbacks know they are stale.
  const authOpIdRef = useRef(0);

  useEffect(() => {
    // Listen to Firebase auth state changes (onIdTokenChanged also fires after reload())
    const unsubscribe = onIdTokenChange(async (firebaseUser) => {
      // Snapshot the current operation ID at the start of this callback.
      // If authOpIdRef changes before we finish (e.g. logout was triggered), we bail out
      // instead of setting stale state that would leave the app stuck on the spinner.
      authOpIdRef.current += 1;
      const opId = authOpIdRef.current;

      if (firebaseUser) {
        // getIdToken can hang or throw after a sign-out race; guard with a timeout.
        let newToken;
        try {
          let getIdTokenTimeoutId;
          newToken = await Promise.race([
            firebaseUser.getIdToken(),
            new Promise((_, reject) => {
              getIdTokenTimeoutId = setTimeout(() => reject(new Error('getIdToken timeout')), 5000);
            }),
          ]);
          clearTimeout(getIdTokenTimeoutId);
        } catch {
          if (opId !== authOpIdRef.current) return;
          setLoading(false);
          return;
        }

        if (opId !== authOpIdRef.current) return;

        // Persist the (possibly refreshed) token so api.js interceptor always has a fresh copy
        await saveItem(STORAGE_KEYS.AUTH_TOKEN, newToken);
        setToken(newToken);
        setUser({
          uid: firebaseUser.uid,
          name: firebaseUser.displayName,
          email: firebaseUser.email || null,
          phoneNumber: firebaseUser.phoneNumber || null,
          emailVerified: firebaseUser.emailVerified,
          photoURL: firebaseUser.photoURL || null,
          hasPasswordProvider: firebaseUser.providerData?.some(p => p?.providerId === 'password') ?? false,
        });

        if (opId !== authOpIdRef.current) return;

        let cpfTimeoutId;
        try {
          const { cpf } = await Promise.race([
            getUserCPF(firebaseUser.uid),
            new Promise((_, reject) => {
              cpfTimeoutId = setTimeout(() => reject(new Error('getUserCPF timeout')), 5000);
            }),
          ]);
          if (opId !== authOpIdRef.current) return;
          setCpfVerified(!!cpf);
        } catch (err) {
          if (opId !== authOpIdRef.current) return;
          setCpfVerified(false);
        } finally {
          clearTimeout(cpfTimeoutId);
        }

        if (opId !== authOpIdRef.current) return;

        // Verify admin status and persist flag to localStorage
        try {
          const adminStatus = await verifyAdmin(newToken);
          if (opId !== authOpIdRef.current) return;
          setIsAdminUser(adminStatus);
          if (adminStatus) {
            try { localStorage.setItem(`@car_alert:admin_${firebaseUser.uid}`, 'true'); } catch (_) {}
          } else {
            try { localStorage.removeItem(`@car_alert:admin_${firebaseUser.uid}`); } catch (_) {}
          }
        } catch {
          if (opId !== authOpIdRef.current) return;
          setIsAdminUser(false);
        }
      } else {
        // Firebase says there is no authenticated user — clear everything including
        // any stale token or user object that may have been saved in localStorage.
        await removeItem(STORAGE_KEYS.AUTH_TOKEN);
        if (opId !== authOpIdRef.current) return;
        setUser(null);
        setToken(null);
        setCpfVerified(null);
        setIsAdminUser(false);
      }

      if (opId !== authOpIdRef.current) return;
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    // Do NOT call setLoading(true) here — doing so causes PublicRoute to unmount
    // LoginScreen while the request is in flight. If the login fails, the error
    // state updates would be dropped on the now-unmounted component and the user
    // would never see the error message.
    const userData = await loginService(email, password);
    setUser(userData);
    const newToken = await refreshToken();
    setToken(newToken);
    return userData;
  };

  const register = async (name, email, password, phoneNumber) => {
    setLoading(true);
    try {
      const userData = await registerService(name, email, password, phoneNumber);
      setUser(userData);
      const newToken = await refreshToken();
      setToken(newToken);
      return userData;
    } finally {
      setLoading(false);
    }
  };

  const registerPhoneUser = async (cpf, cpfName, cpfVerified) => {
    setLoading(true);
    try {
      const userData = await registerPhoneUserService(cpf, cpfName, cpfVerified);
      setUser(userData);
      const newToken = await refreshToken();
      setToken(newToken);
      return userData;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    // Invalidate any in-flight onIdTokenChange callbacks so they cannot set stale state.
    authOpIdRef.current += 1;
    // Clear local state immediately so the UI stops showing the authenticated view
    // even if the network call to Firebase takes time or fails.
    setUser(null);
    setToken(null);
    setCpfVerified(null);
    setIsAdminUser(false);
    setLoading(true);
    try {
      // Give the sign-out up to 8 seconds; proceed even if it times out or errors.
      let logoutTimeoutId;
      await Promise.race([
        logoutService(),
        new Promise((_, reject) => {
          logoutTimeoutId = setTimeout(() => reject(new Error('logout timeout')), 8000);
        }),
      ]);
      clearTimeout(logoutTimeoutId);
    } catch {
      // Even if signOut fails or times out, local state is already cleared.
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    await resendVerificationEmail();
  };

  const refreshUser = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    await currentUser.reload();
    await currentUser.getIdToken(true);
    // onIdTokenChanged will fire and update the user state with the new emailVerified value
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, cpfVerified, setCpfVerified, login, register, registerPhoneUser, logout, resendVerification, refreshUser, isAdminUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
